import IntlFormatMessage from 'intl-messageformat'
import isEmpty from 'lodash.isempty'

import { getCookie, setCookie } from '@core/helpers/cookies'
import { HEADER_NGINX_LANG, HEADER_NGINX_LOCALE } from '@http/headers'
import logger from '@logger'

import { translations } from './module'

/**
 * This function transforms a given message into an array of strings and/or vnodes.
 *
 * @example
 * tokenize('Hello {world}', …) // ['Hello, ', VNode]
 *
 * @example
 * tokenize ('My name is {first} {last}.', …) // ['My name is ', VNode, ' ', VNode, '.']
 *
 * 1. To take advantage of HTML (and components) interpolation, we need to return
 *    an array, and let the Vue compiler do its work. We can't just compute a
 *    string to display in our templates.
 *
 * 2. Note that by doing so, we gain everything Vue has to offer. We can use CSS,
 *    JS and whatever in our translations. We can even use Vue components as-is.
 *
 * 3. We also prevent a huge security breach: XSS issues. Because Vue does the work
 *    for us, we no longer need to manually use `v-html`. So, an attacker would
 *    not be able to inject arbitrary tags (and attributes) in our pages.
 *
 * @param {String} message
 * @param {{ [key: String]: String|VNode }} slots
 *
 * @return {(String|VNode)[]}
 */
const tokenize = (message, slots) => {
  const tokens = []

  let text = ''
  let position = 0

  while (position < message.length) {
    const character = message[position]

    switch (character) {
      case '{':
        tokens.push(text)
        text = ''
        break

      case '}':
        tokens.push(slots[text])
        text = ''
        break

      default:
        text += character
        break
    }

    position += 1
  }

  // Do not forget to push the rest of the message when we reach the end.
  tokens.push(text)

  // Since we do not validate the items pushed into the `tokens` array, we
  // might end up with falsy values (undefined, empty strings…). To avoid
  // poluting our Vue templates with those, let's remove them at the end.
  return tokens.filter(Boolean)
}

const createMessageFormatter =
  ({ locale, formats, showTranslationKeys }) =>
  (definition, values) => {
    if (!definition) {
      // In some cases, when definitions are computed from dynamic values, we
      // may end up in situations where nothing is passed to this function.
      logger.error(new Error('Missing translation definition'), {
        locale,
      })

      // Instead of crashing the application, let's gracefully handle
      // the error by displaying nothing to our end customers.
      return null
    }

    const { id, defaultMessage } = definition

    const trans = translations.getFromLocale(locale)

    try {
      // Do not display those warning in unit tests as we don't have
      // translations there.
      // This will ease the task when trying to check calls on `logger.errors`.
      if (!(id in trans) && process.env.NODE_ENV !== 'test') {
        logger.error(new Error('Missing translation key'), {
          locale,
          id,
        })
      }

      if (showTranslationKeys) {
        return id
      }

      const raw = trans[id] || defaultMessage
      const escaped = raw.replace(/{(\w*), html}/g, "'{$1}'")
      const message = new IntlFormatMessage(escaped, locale, formats)

      return message.format(values)
    } catch {
      // If the key does not have a translation nor a default message,
      return null
    }
  }

const createHtmlFormatter = (formatter) => (definition, values) => {
  const message = formatter(definition, values)

  // If both values are equals, that means the raw formatter returned the key and did
  // not format it at all. We just serve this value, and do not try to process it.
  if (message === definition.id) {
    return message
  }

  if (message) {
    return tokenize(message, values)
  }

  return null
}

const createDateFormatter = (locale) => (date, options) => {
  return Intl.DateTimeFormat(locale, options).format(date)
}

const createNumberFormatter = (locale) => (number, options) => {
  return Intl.NumberFormat(locale, options).format(number)
}

const createListFormatter = (locale) => (list, options) => {
  return new Intl.ListFormat(locale, options).format(list)
}

const createCountryFormatter = (locale) => (countryCode) => {
  if (typeof Intl.DisplayNames !== 'function') {
    return countryCode
  }

  const regionNames = new Intl.DisplayNames([locale], { type: 'region' })

  return regionNames.of(countryCode)
}

const createLanguageFormatter = (locale) => (localeToTranslate) => {
  if (typeof Intl.DisplayNames !== 'function') {
    return localeToTranslate
  }

  const languageNames = new Intl.DisplayNames([locale], { type: 'language' })

  return languageNames.of(localeToTranslate)
}

/**
 * @param {string} locale
 * @param {string} defaultCurrency
 * @returns {createPriceFormatter~inner}
 */
const createPriceFormatter =
  (locale, defaultCurrency) =>
  /**
   * @param {{amount: string, currency?: string}} price
   * @param {Intl.NumberFormatOptions & { symbol?: boolean }} [options]
   * @return {string}
   */
  (price, options = {}) => {
    // TODO [PAYIN-1098] Ensure all prices have been migrated, then remove this
    // fallback.
    if (typeof price === 'string' || typeof price === 'number') {
      logger.error({
        message: '[i18n] Price should follow the Price model',
        price,
      })
      // eslint-disable-next-line no-param-reassign
      price = { amount: price, currency: options.currency }
    }

    if (isEmpty(price)) {
      return ''
    }

    const { symbol = true, ...intlNumberFormatOptions } = options
    const optionsWithOverrides = {
      currency: price.currency || defaultCurrency,
      style: 'currency',
      ...intlNumberFormatOptions,
    }

    const numberFormat = Intl.NumberFormat(locale, optionsWithOverrides)
    const priceWithCurrencySymbol = numberFormat.format(
      parseFloat(price.amount),
    )

    if (symbol) {
      return priceWithCurrencySymbol
    }

    const removeCurrencySymbolRegex = /[^\d.,\s]+/g

    return priceWithCurrencySymbol.replace(removeCurrencySymbolRegex, '').trim()
  }

const createCurrencySymbolFormatter =
  (locale, defaultCurrency) =>
  ({ currency = defaultCurrency, options = {} } = {}) => {
    return createPriceFormatter(locale, currency)({ amount: '0.00' }, options)
      .replace(/\d./g, '')
      .trim()
  }

export const LANG_COOKIE_NAME = 'BM_lang'

// Locale format: fr-fr, en-us,
export const getLocaleFromRequest = (req) => {
  // For the moment, only allow lang override in Back office
  if (req.url.startsWith('/bo_merchant') || req.url.startsWith('/bo_admin')) {
    return getCookie(LANG_COOKIE_NAME, req.headers.cookie) || null
  }

  if (req.headers[HEADER_NGINX_LOCALE]) {
    return req.headers[HEADER_NGINX_LOCALE]
  }

  return null
}

export const getLangFromRequest = (req) => {
  // For the moment, only allow lang override in Back office
  if (req.url.startsWith('/bo_merchant') || req.url.startsWith('/bo_admin')) {
    const cookie = getCookie(LANG_COOKIE_NAME, req.headers.cookie)
    if (cookie) {
      const [lang] = cookie.split('-')

      return lang
    }
  }

  if (req.headers[HEADER_NGINX_LANG]) {
    return req.headers[HEADER_NGINX_LANG]
  }

  return null
}

export const changeLang = (code) => {
  setCookie(LANG_COOKIE_NAME, code)
  window.location.reload()
}

let plugin

export const getPlugin = () => plugin

export const createPlugin = ({ locale, currencyCode, showTranslationKeys }) => {
  const formats = {
    number: {
      currency: {
        style: 'currency',
        currency: currencyCode,
      },
    },
  }

  const formatter = createMessageFormatter({
    locale,
    formats,
    showTranslationKeys,
  })

  formatter.date = createDateFormatter(locale)
  formatter.number = createNumberFormatter(locale)
  formatter.currencySign = createCurrencySymbolFormatter(locale, currencyCode)
  formatter.price = createPriceFormatter(locale, currencyCode)
  formatter.html = createHtmlFormatter(formatter)
  formatter.changeLang = changeLang
  formatter.country = createCountryFormatter(locale)
  formatter.locale = createLanguageFormatter(locale)
  formatter.list = createListFormatter(locale)

  plugin = formatter

  return formatter
}
